import { CommonModule } from '@angular/common';
import { Component, effect, HostListener, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { RouterService } from '@core/services/router.service';
import { TranslationService } from '@core/translation.service';
import { isAgent } from '@features/auth/store/auth.signals';
import { CustomerApiService } from '@features/customer/services/customer-api.service';
import { activeCustomerId } from '@features/customer/store/work-with-customer.signals';
import { WorkWithCustomerStore } from '@features/customer/store/work-with-customer.store';
import { NotificationRedirect } from '@features/notifications/models/notification-item';
import { NotificationTabs } from '@features/notifications/models/notification-tabs';
import { NotificationBellService } from '@features/notifications/services/notification-bell.service';
import { NotificationsService } from '@features/notifications/services/notifications.service';
import { groupedNotifications, hasMarkAsRead, isLoading, isUnreadOnly, isZeroState, selectedTab, tabBadges } from '@features/notifications/store/services/notification.store';
import { ScrollService } from '@layout/scroll-to-top/scroll.service';
import { PnycButtonComponent } from '@ui-kit/pnyc-button/pnyc-button.component';
import { PnycIconComponent } from '@ui-kit/pnyc-icon/pnyc-icon.component';
import { PnycSlideToggleComponent } from '@ui-kit/pnyc-slide-toggle/pnyc-slide-toggle.component';
import { PnycTabItem } from '@ui-kit/pnyc-tabs/models/pnyc-tab-item';
import { PnycTabsComponent } from '@ui-kit/pnyc-tabs/pnyc-tabs.component';
import { NotificationItemComponent } from '../notification-item/notification-item.component';
import { NOTIFICATION_GROUP_NAMES } from './constants/notification-groups.constants';
import { locale as english } from './i18n/en';

const BELL_BODY_SELECTOR = '.pnyc-notification-bell-body';

@Component({
    selector: 'notification-bell',
    imports: [
        CommonModule,
        TranslateModule,
        PnycButtonComponent,
        PnycIconComponent,
        PnycSlideToggleComponent,
        PnycTabsComponent,
        NotificationItemComponent
    ],
    templateUrl: './notification-bell.component.html',
    styleUrl: './notification-bell.component.scss'
})
export class NotificationBellComponent {
    public hasCloseButton = input<boolean>(false);
    public shouldScrollToTop = input<boolean>(false);
    public closeEvent = output<void>();

    public tabs: PnycTabItem<NotificationTabs>[] = [];

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public GROUP_NAMES = NOTIFICATION_GROUP_NAMES;

    public readonly notificationGroups = groupedNotifications;
    public readonly isUnreadOnly = isUnreadOnly;
    public readonly isZeroState = isZeroState;
    public readonly hasMarkAllAsRead = hasMarkAsRead;
    public readonly isLoading = isLoading;
    public readonly selectedTab = selectedTab;

    private readonly translationLoader = inject(TranslationService);
    private readonly notificationsService = inject(NotificationsService);
    private readonly notificationBellService = inject(NotificationBellService);
    private readonly scrollService = inject(ScrollService);
    private readonly routerService = inject(RouterService);
    private readonly customerApiService = inject(CustomerApiService);
    private readonly workWithCustomerStore = inject(WorkWithCustomerStore);

    constructor() {
        this.translationLoader.loadTranslations(english);

        this.notificationBellService.open();

        effect(() => {
            if (this.shouldScrollToTop()) {
                this.scrollToTop();
            }
        });

        effect(() => {
            const badges = tabBadges();

            this.tabs = [
                { text: 'NOTIFICATION_BELL.ALL', value: NotificationTabs.All, badge: { badgeCountAlert: badges[NotificationTabs.All], badgeCountAlertLimit: 9 } },
                { text: 'NOTIFICATION_BELL.MY_ACTIVITY', value: NotificationTabs.Agent, badge: { badgeCountAlert: badges[NotificationTabs.Agent], badgeCountAlertLimit: 9 } },
                { text: 'NOTIFICATION_BELL.CUSTOMER_ACTIVITY', value: NotificationTabs.Customer, badge: { badgeCountAlert: badges[NotificationTabs.Customer], badgeCountAlertLimit: 9 } },
            ];
        });
    }

    @HostListener('document:keydown.escape')
    onEscapeKey() {
        this.closeEvent.emit();
    }

    public toggleUnread(): void {
        this.notificationBellService.toggleUnread();
    }

    public onTabChange(tab: NotificationTabs): void {
        selectedTab.set(tab);
        this.scrollToTop();
    }

    public toggleCollapsed(notificationId: number): void {
        this.notificationsService.toggleCollapsed(notificationId);
    }

    public markAsRead(notificationId: number, isGroup: boolean, shouldPreventUnview?: boolean): void {
        this.notificationsService.viewUnviewNotification(notificationId, isGroup, shouldPreventUnview);
    }

    public markAllAsRead(): void {
        this.notificationsService.markAllNotificationsAsRead();
    }

    public async onRedirect(redirect: NotificationRedirect): Promise<void> {
        if (!redirect.path) {
            return;
        }

        await this.selectRedirectCustomer(redirect);

        const [route, anchorId] = redirect.path.split('#');
        const url = route.startsWith('/') ? route : `/${route}`;

        if (anchorId && url === this.routerService.currentUrl()) {
            this.scrollService.scrollToElementById(anchorId, 'smooth');
        } else {
            void this.routerService.navigateByUrl(url, {
                destinationState: anchorId ? { scrollToId: anchorId } : undefined
            });
        }

        this.closeEvent.emit();
    }

    private async selectRedirectCustomer({ customerId, collaborationId }: NotificationRedirect): Promise<void> {
        if (customerId == null || !isAgent() || activeCustomerId() === customerId) {
            return;
        }

        try {
            const customer = await this.customerApiService.getWorkWithCustomerById(customerId);

            this.workWithCustomerStore.setActiveCustomer({ ...customer, collaborationId: collaborationId || undefined });
        } catch { }
    }

    private scrollToTop(): void {
        this.scrollService.scrollInContainer(BELL_BODY_SELECTOR, { top: 0, behavior: 'smooth' });
    }
}
